import { useEffect, useMemo, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { FEATURES } from '@/config/features';
import { type BgtUserDto, listBgtUsers } from '@/lib/api/bgt-users';
import { CredentialsDialog } from '@/components/residents/CredentialsDialog';
import {
  VOICE_LOGIN_ID_HINT,
  VOICE_LOGIN_ID_PATTERN,
  VoiceLoginIdField,
} from '@/components/residents/VoiceLoginIdField';
import type { IssuedCredentials } from '@/lib/api/credentials';
import {
  type BgtUserInputErrors,
  normalizeBirthInput,
  validateBgtUserInput,
} from '@/lib/bgt-user-input';
import { createResident, type ResidentType, updateResident } from '@/lib/api/residents';
import { listRooms } from '@/lib/api/rooms';
import { listVoiceUsers, type VoiceUserDto } from '@/lib/api/voice-users';

/** 수정 모드 시 dialog에 전달할 입소자 최소 정보 — CRUD/Dashboard 응답 양쪽 호환 */
export interface ResidentFormInitial {
  id: string;
  userId: string | null;
  roomId: string | null;
  voiceUserId: number | null;
  type: ResidentType;
}

// 외래 선택지는 협의 완료 전까지 감춘다 — FEATURES.outpatient 참고.
// 열어두면 쓸 수 없는 구분으로 잘못 등록될 뿐이다(실제 오지정 사례 있음).
const TYPES: { value: ResidentType; label: string }[] = [
  { value: 'RESIDENT', label: '입소' },
  ...(FEATURES.outpatient
    ? ([
        { value: 'OUTPATIENT_A', label: '외래 — 간이검사' },
        { value: 'OUTPATIENT_B', label: '외래 — 퇴소자 추적' },
      ] as { value: ResidentType; label: string }[])
    : []),
];

const PAGE_SIZE = 20;

/** 연결 안 함 / 기존 계정 연결 / 새로 만들기 — 두 프로그램이 공유하는 선택지 */
type AccountMode = 'none' | 'link' | 'create';

const ACCOUNT_MODES: { value: AccountMode; label: string }[] = [
  { value: 'none', label: '연결 안 함' },
  { value: 'link', label: '기존 계정' },
  { value: 'create', label: '새로 만들기' },
];

const schema = z.object({
  userId: z
    .string()
    .uuid({ message: '브레인가드테라피 회원을 선택해 주세요' })
    .optional()
    .or(z.literal('')),
  roomId: z.string().uuid().optional().or(z.literal('')),
  /** voicecheck.users.id — 입소자 개인 매핑. 호실과 무관 */
  voiceUserId: z.number().int().positive().optional().nullable(),
  type: z.enum(['RESIDENT', 'OUTPATIENT_A', 'OUTPATIENT_B']),
});

type FormValues = z.infer<typeof schema>;

/** 두 프로그램이 같은 모양으로 쓰는 선택 줄 */
function ModeSelector({
  label,
  value,
  onChange,
}: {
  label: string;
  value: AccountMode;
  onChange: (next: AccountMode) => void;
}) {
  return (
    <Stack spacing={1}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={value}
        aria-label={`${label} 처리 방식`}
        onChange={(_, next: AccountMode | null) => {
          if (next) onChange(next);
        }}
      >
        {ACCOUNT_MODES.map((m) => (
          <ToggleButton key={m.value} value={m.value}>
            {m.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Stack>
  );
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** 지정하면 수정 모드 */
  initial?: ResidentFormInitial;
}

function nextPageParam(last: { page: number; limit: number; total: number }): number | undefined {
  const loaded = last.page * last.limit;
  return loaded < last.total ? last.page + 1 : undefined;
}

/** 스크롤이 하단 근처면 다음 페이지 로드 */
function onListboxScroll(
  event: React.UIEvent<HTMLUListElement>,
  opts: { hasNextPage: boolean; isFetchingNextPage: boolean; fetchNextPage: () => void },
): void {
  const list = event.currentTarget;
  if (list.scrollTop + list.clientHeight < list.scrollHeight - 48) return;
  if (!opts.hasNextPage || opts.isFetchingNextPage) return;
  opts.fetchNextPage();
}

/**
 * 수정 모드: voiceUserId로 페이지 순회해 계정 복원
 * @returns {Promise<VoiceUserDto|null>}
 */
async function findVoiceUserById(id: number): Promise<VoiceUserDto | null> {
  let page = 1;
  for (;;) {
    const res = await listVoiceUsers({ page, limit: PAGE_SIZE, unmappedOnly: false });
    const found = res.items.find((u) => u.id === id);
    if (found) return found;
    if (page * res.limit >= res.total) return null;
    page += 1;
  }
}

export function ResidentFormDialog({ open, onClose, initial }: Props) {
  const queryClient = useQueryClient();
  const isEdit = !!initial;
  const [userSearch, setUserSearch] = useState('');
  /**
   * 두 프로그램 모두 같은 3지선다를 쓴다 — 연결 안 함 / 기존 계정 / 새로 만들기.
   * 한쪽만 다른 모양이면 같은 폼 안에서 두 가지 사고방식을 요구하게 된다.
   *
   * 생성은 전부 **저장 시점**에 한다. 고르는 즉시 만들어 버리면 등록을 취소했을 때
   * 아무에게도 연결되지 않은 계정이 DB에 그대로 남는다.
   */
  const [bgtMode, setBgtMode] = useState<AccountMode>('link');
  const [voiceMode, setVoiceMode] = useState<AccountMode>('link');
  const [newBgt, setNewBgt] = useState({ patientCode: '', birthDate: '', birthConfirm: '' });
  const [newBgtErrors, setNewBgtErrors] = useState<BgtUserInputErrors>({});
  /** 직접 입력한 보이스웰니스 아이디. null이면 환자번호에서 자동으로 만든다 */
  const [voiceLoginId, setVoiceLoginId] = useState<string | null>(null);
  const [voiceLoginIdError, setVoiceLoginIdError] = useState<string | undefined>();
  /** 등록 직후 한 번 보여줄 로그인 정보 */
  const [issued, setIssued] = useState<IssuedCredentials | null>(null);
  const [voiceSearch, setVoiceSearch] = useState('');
  const [selectedBgtUser, setSelectedBgtUser] = useState<BgtUserDto | null>(null);
  const [selectedVoiceUser, setSelectedVoiceUser] = useState<VoiceUserDto | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      userId: initial?.userId ?? '',
      roomId: initial?.roomId ?? '',
      voiceUserId: initial?.voiceUserId ?? null,
      type: initial?.type ?? 'RESIDENT',
    },
  });

  const watchedRoomId = watch('roomId');

  useEffect(() => {
    if (open) {
      reset({
        userId: initial?.userId ?? '',
        roomId: initial?.roomId ?? '',
        voiceUserId: initial?.voiceUserId ?? null,
        type: initial?.type ?? 'RESIDENT',
      });
      setUserSearch('');
      setVoiceSearch('');
      setSelectedBgtUser(null);
      setSelectedVoiceUser(null);
      setSubmitError(null);
      setBgtMode('link');
      setVoiceMode('link');
      setNewBgt({ patientCode: '', birthDate: '', birthConfirm: '' });
      setNewBgtErrors({});
      setVoiceLoginId(null);
      setVoiceLoginIdError(undefined);
      setIssued(null);
    }
  }, [open, initial, reset]);

  const roomsQuery = useQuery({
    queryKey: ['rooms'],
    queryFn: listRooms,
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  const bgtUsersQuery = useInfiniteQuery({
    queryKey: ['bgt-users', 'infinite', userSearch, !isEdit],
    queryFn: ({ pageParam }) =>
      listBgtUsers({
        page: pageParam,
        limit: PAGE_SIZE,
        search: userSearch || undefined,
        unmappedOnly: !isEdit,
      }),
    initialPageParam: 1,
    getNextPageParam: nextPageParam,
    enabled: open && !isEdit,
  });

  const voiceUsersQuery = useInfiniteQuery({
    queryKey: ['voice-users', 'infinite', voiceSearch, isEdit],
    queryFn: ({ pageParam }) =>
      listVoiceUsers({
        page: pageParam,
        limit: PAGE_SIZE,
        search: voiceSearch || undefined,
        unmappedOnly: !isEdit,
      }),
    initialPageParam: 1,
    getNextPageParam: nextPageParam,
    enabled: open,
  });

  // 수정 모드: 연결된 voiceUserId 복원 (limit 페이지 순회)
  useEffect(() => {
    if (!open || !initial?.voiceUserId || selectedVoiceUser) return;

    const fromLoaded = voiceUsersQuery.data?.pages
      .flatMap((p) => p.items)
      .find((u) => u.id === initial.voiceUserId);
    if (fromLoaded) {
      setSelectedVoiceUser(fromLoaded);
      return;
    }
    if (voiceUsersQuery.isLoading || voiceUsersQuery.isFetching) return;

    let cancelled = false;
    void findVoiceUserById(initial.voiceUserId).then((found) => {
      if (!cancelled && found) setSelectedVoiceUser(found);
    });
    return () => {
      cancelled = true;
    };
  }, [
    open,
    initial?.voiceUserId,
    voiceUsersQuery.data,
    voiceUsersQuery.isLoading,
    voiceUsersQuery.isFetching,
    selectedVoiceUser,
  ]);

  const submitMutation = useMutation({
    mutationFn: async (values: FormValues) => {
      if (isEdit && initial) {
        await updateResident(initial.id, {
          roomId: values.roomId || null,
          type: values.type,
          voiceUserId: values.voiceUserId ?? null,
        });
        return null;
      }

      // 회원 생성 · 입소자 등록 · 계정 발급을 한 요청으로 보낸다. 어느 단계가
      // 실패하든 서버가 전부 되돌리므로, 여기서 부분 성공을 다룰 일이 없다.
      const created = await createResident({
        userId: bgtMode === 'link' ? values.userId || undefined : undefined,
        newBgtUser:
          bgtMode === 'create'
            ? { patientCode: newBgt.patientCode.trim(), birthDate: newBgt.birthDate }
            : undefined,
        roomId: values.roomId || undefined,
        voiceUserId: voiceMode === 'link' ? (values.voiceUserId ?? undefined) : undefined,
        issueVoiceAccount: voiceMode === 'create' || undefined,
        voiceLoginId: voiceMode === 'create' ? (voiceLoginId ?? undefined) : undefined,
        type: values.type,
      });

      return {
        bgtPatientCode: created.user?.patientCode ?? null,
        bgtBirthDate: created.user?.birthDate ?? null,
        voiceLoginId: created.voiceLoginId,
        voicePasswordRetrievable: false,
        voiceTemporaryPassword: created.voiceTemporaryPassword,
      };
    },
    onSuccess: (credentials) => {
      // 상세 drawer·보이스 탭이 구 voiceUserId 캐시를 쓰지 않도록 일괄 무효화
      void queryClient.invalidateQueries({ queryKey: ['residents'] });
      void queryClient.invalidateQueries({ queryKey: ['bgt-users'] });
      void queryClient.invalidateQueries({ queryKey: ['voice-users'] });
      if (credentials) {
        // 폼은 닫고 로그인 정보만 남긴다. 발급된 비밀번호는 이 화면에서만 볼 수 있다.
        setIssued(credentials);
      } else {
        onClose();
      }
    },
    onError: (err: unknown) => {
      if (err instanceof AxiosError) {
        const msg = (err.response?.data as { message?: string | string[] } | undefined)?.message;
        setSubmitError(
          Array.isArray(msg) ? msg.join(', ') : (msg ?? '요청 처리 중 오류가 발생했습니다'),
        );
      } else {
        setSubmitError('알 수 없는 오류가 발생했습니다');
      }
    },
  });

  const onSubmit = handleSubmit((values) => {
    setSubmitError(null);
    if (!isEdit && bgtMode === 'create') {
      const found = validateBgtUserInput({ ...newBgt, requireConfirm: true });
      setNewBgtErrors(found);
      if (Object.keys(found).length > 0) return;
    } else {
      setNewBgtErrors({});
    }

    if (!isEdit && voiceMode === 'create') {
      // 근거도 없고 직접 입력도 없으면 서버가 발급을 거절하고 등록까지 되돌아간다.
      // 저장을 눌러본 뒤에 알게 되지 않도록 여기서 막는다.
      if (voiceLoginId !== null && !VOICE_LOGIN_ID_PATTERN.test(voiceLoginId)) {
        setVoiceLoginIdError(VOICE_LOGIN_ID_HINT);
        return;
      }
      if (voiceLoginId === null && !voiceIdSource.patientCode && !voiceIdSource.roomNumber) {
        setVoiceLoginIdError('아이디를 직접 입력하거나, 회원·호실을 선택해 주세요');
        return;
      }
    }
    setVoiceLoginIdError(undefined);

    submitMutation.mutate(values);
  });

  const bgtOptions = useMemo(
    () => bgtUsersQuery.data?.pages.flatMap((p) => p.items) ?? [],
    [bgtUsersQuery.data],
  );

  /** 아이디 자동 발급의 근거 — 폼에서 고른(또는 입력한) 값 */
  const voiceIdSource = useMemo(() => {
    const patientCode =
      bgtMode === 'create'
        ? newBgt.patientCode.trim() || null
        : (selectedBgtUser?.patientCode ?? null);
    const roomNumber =
      (roomsQuery.data ?? []).find((r) => r.id === watchedRoomId)?.roomNumber ?? null;
    return { patientCode, roomNumber };
  }, [bgtMode, newBgt.patientCode, selectedBgtUser, roomsQuery.data, watchedRoomId]);

  const voiceOptions = useMemo(() => {
    const items = voiceUsersQuery.data?.pages.flatMap((p) => p.items) ?? [];
    if (selectedVoiceUser && !items.some((u) => u.id === selectedVoiceUser.id)) {
      return [selectedVoiceUser, ...items];
    }
    return items;
  }, [voiceUsersQuery.data, selectedVoiceUser]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isEdit ? '입소자 수정' : '입소자 등록'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }} component="form" onSubmit={onSubmit} noValidate>
          {!isEdit && (
            <ModeSelector label="브레인가드테라피 회원" value={bgtMode} onChange={setBgtMode} />
          )}

          {!isEdit && bgtMode === 'link' && (
            <Controller
              control={control}
              name="userId"
              render={({ field }) => (
                <Autocomplete<BgtUserDto, false, false, false>
                  options={bgtOptions}
                  loading={bgtUsersQuery.isLoading || bgtUsersQuery.isFetchingNextPage}
                  value={selectedBgtUser}
                  isOptionEqualToValue={(opt, v) => opt.id === v.id}
                  getOptionLabel={(opt) => {
                    const code = opt.patientCode ?? opt.id.slice(0, 8);
                    const birth = opt.birthDate ? ` · ${opt.birthDate}` : '';
                    const age = opt.age !== null && opt.age !== undefined ? ` (${opt.age}세)` : '';
                    const name = opt.name ? ` · ${opt.name}` : '';
                    return `${code}${birth}${age}${name}`;
                  }}
                  filterOptions={(options) => options}
                  getOptionDisabled={(opt) => opt.alreadyMapped}
                  slotProps={{
                    listbox: {
                      onScroll: (e) =>
                        onListboxScroll(e, {
                          hasNextPage: !!bgtUsersQuery.hasNextPage,
                          isFetchingNextPage: bgtUsersQuery.isFetchingNextPage,
                          fetchNextPage: () => {
                            void bgtUsersQuery.fetchNextPage();
                          },
                        }),
                      style: { maxHeight: 280 },
                    },
                  }}
                  onInputChange={(_, value, reason) => {
                    if (reason === 'input') setUserSearch(value);
                    if (reason === 'clear') setUserSearch('');
                  }}
                  onChange={(_, value) => {
                    setSelectedBgtUser(value);
                    field.onChange(value?.id ?? '');
                  }}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      {(() => {
                        const code = option.patientCode ?? option.id.slice(0, 8);
                        const birth = option.birthDate ? ` · ${option.birthDate}` : '';
                        const age =
                          option.age !== null && option.age !== undefined
                            ? ` (${option.age}세)`
                            : '';
                        const name = option.name ? ` · ${option.name}` : '';
                        return `${code}${birth}${age}${name}`;
                      })()}
                    </li>
                  )}
                  noOptionsText={
                    bgtUsersQuery.isFetchingNextPage ? (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CircularProgress size={14} />
                        <Typography variant="body2">불러오는 중…</Typography>
                      </Stack>
                    ) : (
                      '결과 없음'
                    )
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="브레인가드테라피 회원"
                      error={!!errors.userId}
                      helperText={
                        errors.userId?.message ??
                        '환자번호 또는 이름으로 검색 · 스크롤 시 추가 로드'
                      }
                    />
                  )}
                />
              )}
            />
          )}

          {!isEdit && bgtMode === 'create' && (
            <Stack spacing={2} sx={{ pl: 1, borderLeft: 2, borderColor: 'divider' }}>
              <Alert severity="info" sx={{ py: 0.5 }}>
                환자번호와 생년월일이 곧 앱 로그인 정보입니다. 한 글자라도 다르면 대상자가 앱에
                로그인할 수 없습니다.
              </Alert>
              <TextField
                label="환자번호"
                size="small"
                value={newBgt.patientCode}
                onChange={(e) => setNewBgt((v) => ({ ...v, patientCode: e.target.value }))}
                error={!!newBgtErrors.patientCode}
                helperText={newBgtErrors.patientCode ?? '앱 로그인 ID로 쓰입니다'}
              />
              <TextField
                label="생년월일"
                size="small"
                value={newBgt.birthDate}
                onChange={(e) =>
                  setNewBgt((v) => ({ ...v, birthDate: normalizeBirthInput(e.target.value) }))
                }
                error={!!newBgtErrors.birthDate}
                helperText={newBgtErrors.birthDate ?? '숫자 8자리 (예: 19450312)'}
                slotProps={{ htmlInput: { inputMode: 'numeric' } }}
              />
              <TextField
                label="생년월일 확인"
                size="small"
                value={newBgt.birthConfirm}
                onChange={(e) =>
                  setNewBgt((v) => ({ ...v, birthConfirm: normalizeBirthInput(e.target.value) }))
                }
                error={!!newBgtErrors.birthConfirm}
                helperText={newBgtErrors.birthConfirm ?? '오타 방지를 위해 한 번 더 입력합니다'}
                slotProps={{ htmlInput: { inputMode: 'numeric' } }}
              />
            </Stack>
          )}

          <Controller
            control={control}
            name="roomId"
            render={({ field }) => (
              <TextField
                {...field}
                select
                label="호실"
                error={!!errors.roomId}
                helperText={errors.roomId?.message}
              >
                <MenuItem value="">(미배정)</MenuItem>
                {(roomsQuery.data ?? []).map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    {r.roomNumber}호 ({r.floor}층)
                  </MenuItem>
                ))}
              </TextField>
            )}
          />

          {!isEdit && (
            <>
              <ModeSelector label="보이스웰니스 계정" value={voiceMode} onChange={setVoiceMode} />
              {voiceMode === 'create' && (
                <>
                  <VoiceLoginIdField
                    patientCode={voiceIdSource.patientCode}
                    roomNumber={voiceIdSource.roomNumber}
                    value={voiceLoginId}
                    onChange={(next) => {
                      setVoiceLoginId(next);
                      setVoiceLoginIdError(undefined);
                    }}
                    error={voiceLoginIdError}
                  />
                  <Alert severity="info" sx={{ py: 0.5 }}>
                    저장하면 계정을 만들어 바로 연결하고, 임시 비밀번호를 보여드립니다. 발급에
                    실패하면 등록 자체가 취소되므로 반쪽짜리로 남지 않습니다.
                  </Alert>
                </>
              )}
            </>
          )}

          {(isEdit || voiceMode === 'link') && (
            <Controller
              control={control}
              name="voiceUserId"
              render={({ field }) => (
                <Autocomplete<VoiceUserDto, false, false, false>
                  options={voiceOptions}
                  loading={voiceUsersQuery.isLoading || voiceUsersQuery.isFetchingNextPage}
                  value={selectedVoiceUser}
                  isOptionEqualToValue={(opt, v) => opt.id === v.id}
                  getOptionLabel={(opt) => `${opt.loginId} · ${opt.name}`}
                  filterOptions={(options) => options}
                  getOptionDisabled={(opt) => opt.alreadyMapped && opt.id !== selectedVoiceUser?.id}
                  slotProps={{
                    listbox: {
                      onScroll: (e) =>
                        onListboxScroll(e, {
                          hasNextPage: !!voiceUsersQuery.hasNextPage,
                          isFetchingNextPage: voiceUsersQuery.isFetchingNextPage,
                          fetchNextPage: () => {
                            void voiceUsersQuery.fetchNextPage();
                          },
                        }),
                      style: { maxHeight: 280 },
                    },
                  }}
                  onInputChange={(_, value, reason) => {
                    if (reason === 'input') setVoiceSearch(value);
                    if (reason === 'clear') setVoiceSearch('');
                  }}
                  onChange={(_, value) => {
                    setSelectedVoiceUser(value);
                    field.onChange(value?.id ?? null);
                  }}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      {option.loginId} · {option.name}
                    </li>
                  )}
                  noOptionsText={
                    voiceUsersQuery.isFetchingNextPage ? (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CircularProgress size={14} />
                        <Typography variant="body2">불러오는 중…</Typography>
                      </Stack>
                    ) : (
                      '결과 없음'
                    )
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="보이스웰니스 계정 (선택)"
                      error={!!errors.voiceUserId}
                      helperText={
                        errors.voiceUserId?.message ?? '입소자 개인 계정 · 스크롤 시 추가 로드'
                      }
                    />
                  )}
                />
              )}
            />
          )}

          <Controller
            control={control}
            name="type"
            render={({ field }) => (
              <TextField
                {...field}
                select
                label="대상자 구분"
                error={!!errors.type}
                helperText={errors.type?.message}
              >
                {TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />

          {submitError && <Alert severity="error">{submitError}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>취소</Button>
        <Button variant="contained" onClick={onSubmit} disabled={isSubmitting}>
          {isSubmitting ? '저장 중…' : isEdit ? '수정' : '등록'}
        </Button>
      </DialogActions>

      {/*
        등록 직후 1회 — 보이스웰니스 비밀번호가 보이는 유일한 화면.
        이 창을 닫을 때 폼도 함께 닫는다.
      */}
      <CredentialsDialog
        open={issued !== null}
        title="등록되었습니다 · 로그인 정보"
        credentials={issued}
        onClose={() => {
          setIssued(null);
          onClose();
        }}
      />
    </Dialog>
  );
}
